import styled, { css } from 'styled-components';
import { themeColor } from '~/common/utils/theme';
import { ControlsPosition } from './PdfViewer';

type ContainerProps = {
  controlsPosition?: ControlsPosition;
  noPadding?: boolean;
  noScroll?: boolean;
};

type ControlsContainerProps = {
  position?: ControlsPosition;
};

export const Container = styled.div<ContainerProps>`
  display: flex;
  width: 100%;
  height: 100%;
  background-color: ${themeColor('background.secondary')};
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  position: relative;

  ${({ controlsPosition, noPadding }) => {
    switch (controlsPosition) {
      case 'left':
      case 'right':
        return css`
          flex-direction: row;
          padding: 0;
        `;
      case 'top':
      case 'bottom':
      default:
        return css`
          flex-direction: column;
          align-items: center;
          padding: ${noPadding ? 0 : '20px'};
        `;
    }
  }}
`;

export const DocumentContainer = styled.div<ContainerProps>`
  flex: 1;
  overflow: auto;
  display: flex;
  justify-content: center;
  overflow: ${({ noScroll }) => (noScroll ? 'hidden' : 'auto')};

  ${({ controlsPosition }) => {
    switch (controlsPosition) {
      case 'left':
        return css`
          padding-left: 40px;
        `;
      case 'right':
        return css`
          padding-right: 40px;
        `;
      default:
        return css`
          width: 100%;
          height: 100%;
        `;
    }
  }}

  .react-pdf__Document {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .react-pdf__Page {
    box-shadow: 0 0 8px rgba(0, 0, 0, 0.2);
  }

  .react-pdf__Page__canvas {
    border-radius: 4px;
  }
`;

export const ControlsContainer = styled.div<ControlsContainerProps>`
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${themeColor('background.primary')};
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

  ${({ position }) => {
    switch (position) {
      case 'top':
        return css`
          width: 100%;
          margin-bottom: 10px;
          padding: 10px;
        `;
      case 'bottom':
      default:
        return css`
          width: 100%;
          margin-top: 10px;
          padding: 10px;
        `;
    }
  }}
`;

export const CompactControlsContainer = styled.div<ControlsContainerProps>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: ${themeColor('background.primary')};
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 8px 4px;
  z-index: 10;

  ${({ position }) => {
    switch (position) {
      case 'left':
        return css`
          position: absolute;
          left: 0;
          top: 50%;
          transform: translateY(-50%);
          border-radius: 0 4px 4px 0;
          padding: 12px 6px;
        `;
      case 'right':
        return css`
          position: absolute;
          right: 0;
          top: 50%;
          transform: translateY(-50%);
          border-radius: 4px 0 0 4px;
          padding: 12px 6px;
        `;
      default:
        return '';
    }
  }}
`;

export const CompactPageInfo = styled.span`
  margin: 12px 0;
  padding: 6px 0;
  font-size: 12px;
  font-weight: 600;
  color: ${themeColor('element.secondary')};
  text-align: center;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 20px;
    height: 1px;
    background-color: ${themeColor('divider.primary')};
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 20px;
    height: 1px;
    background-color: ${themeColor('divider.primary')};
  }
`;

export const PageNavigation = styled.div`
  display: flex;
  align-items: center;
  margin: 0 20px;
`;

export const PageInfo = styled.span`
  margin: 0 10px;
  font-size: 14px;
  color: ${themeColor('element.secondary')};
`;

export const Button = styled.button`
  background-color: ${themeColor('background.tertiary')};
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  margin: 0 5px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${themeColor('element.disabled')};
  }

  &:disabled {
    background-color: ${themeColor('background.tertiary')};
    color: ${themeColor('element.placeholder')};
    cursor: not-allowed;
  }
`;

export const CompactButton = styled.button`
  background-color: ${themeColor('background.tertiary')};
  border: none;
  border-radius: 4px;
  padding: 4px;
  width: 24px;
  height: 24px;
  margin: 2px 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${themeColor('element.disabled')};
  }

  &:disabled {
    background-color: ${themeColor('background.tertiary')};
    color: ${themeColor('element.placeholder')};
    cursor: not-allowed;
  }
`;

export const ZoomControls = styled.div`
  display: flex;
  align-items: center;
`;

export const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 300px;
`;

export const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 300px;
  color: ${themeColor('negative.primary')};
  text-align: center;

  p {
    margin-top: 10px;
    font-size: 16px;
  }
`;

export const IframeContainer = styled.div`
  width: 100%;
  height: 100%;
  min-height: 500px;

  iframe {
    width: 100%;
    height: 100%;
    border: none;
    border-radius: 4px;
  }
`;
